import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { PatientRecord } from '@/features/encounters/api';
import { api, type PageMeta } from '@/lib/api';
import type {
  AllergyGroup,
  AllergySeverity,
  AppAccountAnswer,
  BloodTypeValue,
  CreatePatientInput,
  DisclosureCheck,
  DisclosureEntry,
  DisclosureRequester,
  DisclosureResult,
  DisclosureTopic,
  DuplicateCheckResult,
  DuplicatePair,
  IdentityType,
  PatientDetail,
  PatientAppSignInMethods,
  PatientSummary,
  Payer,
  UpdatePatientInput,
} from './types';

export interface PatientListQuery {
  q?: string;
  page?: number;
  pageSize?: number;
}

export function usePatients(query: PatientListQuery) {
  return useQuery({
    queryKey: ['patients', query],
    queryFn: ({ signal }) =>
      api.get<PatientSummary[], PageMeta>('/patients', {
        signal,
        query: { ...query },
      }),
    placeholderData: keepPreviousData,
  });
}

export function usePatient(id: string | undefined) {
  return useQuery({
    queryKey: ['patient', id],
    queryFn: async ({ signal }) =>
      (await api.get<PatientDetail>(`/patients/${id}`, { signal })).data,
    enabled: !!id,
  });
}

/** RL-1325: sign-in methods for every app account linked to the record. */
export function usePatientAppSignInMethods(patientId: string, enabled = true) {
  return useQuery({
    queryKey: ['patient-app-sign-in', patientId],
    queryFn: async ({ signal }) =>
      (
        await api.get<PatientAppSignInMethods>(
          `/patients/${patientId}/app-sign-in`,
          { signal },
        )
      ).data,
    enabled,
    retry: false,
  });
}

/** RL-1325: disable or re-enable password sign-in with an audited reason. */
export function usePatientAppSignInAction(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['patient-app-sign-in-action', patientId],
    mutationFn: async (input: {
      accountId: string;
      action: 'disable' | 'enable';
      reason: string;
    }) =>
      (
        await api.post<PatientAppSignInMethods>(
          `/patients/${patientId}/app-sign-in/${input.accountId}/password/${input.action}`,
          { reason: input.reason },
        )
      ).data,
    onSuccess: (methods) => {
      qc.setQueryData(['patient-app-sign-in', patientId], methods);
    },
  });
}

export function useDuplicateCheck(params: {
  identityType?: IdentityType;
  identityValue?: string;
  issuingCountry?: string;
  fullName?: string;
  phone?: string;
}) {
  const enabled = !!(
    params.identityValue ||
    (params.fullName && params.fullName.length >= 3) ||
    params.phone
  );
  return useQuery({
    queryKey: ['patient-duplicates', params],
    queryFn: async ({ signal }) =>
      (
        await api.get<DuplicateCheckResult>('/patients/duplicate-check', {
          signal,
          query: { ...params },
        })
      ).data,
    enabled,
    retry: false,
    staleTime: 10_000,
  });
}

export function useCreatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePatientInput) =>
      (await api.post<PatientDetail>('/patients', input)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['patients'] }),
  });
}

export const updatePatientKey = (id: string) => ['patient-update', id] as const;

export function useUpdatePatient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: updatePatientKey(id),
    mutationFn: async (input: UpdatePatientInput) =>
      (await api.patch<PatientDetail>(`/patients/${id}`, input)).data,
    onSuccess: (patient) => {
      qc.setQueryData(['patient', id], patient);
      void qc.invalidateQueries({ queryKey: ['patients'] });
      // The record page's profile card reads GET /patients/:id/record.
      void qc.invalidateQueries({ queryKey: ['patient-record', id] });
    },
  });
}

export function usePayers(enabled = true) {
  return useQuery({
    queryKey: ['payers'],
    queryFn: async ({ signal }) =>
      (await api.get<Payer[]>('/payers', { signal })).data,
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function usePossibleDuplicates(enabled = true) {
  return useQuery({
    queryKey: ['patient-duplicate-pairs'],
    queryFn: async ({ signal }) =>
      (await api.get<DuplicatePair[]>('/patients/duplicates', { signal })).data,
    enabled,
  });
}

export function useDisclosuresToday() {
  return useQuery({
    queryKey: ['patient-disclosures'],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ date: string; entries: DisclosureEntry[] }>(
          '/patients/disclosures',
          { signal },
        )
      ).data,
  });
}

/** Front desk patient actions (RL-721): eKYC, cover, disclosures, merge. */
export function usePatientActions() {
  const qc = useQueryClient();
  const refresh = (p?: PatientDetail) => {
    if (p) qc.setQueryData(['patient', p.id], p);
    void qc.invalidateQueries({ queryKey: ['patients'] });
    void qc.invalidateQueries({ queryKey: ['queue'] });
    void qc.invalidateQueries({ queryKey: ['patient-record'] });
  };
  /** Every view that lists allergies or checks them at prescribing. */
  const allergiesChanged = (p?: PatientDetail) => {
    refresh(p);
    void qc.invalidateQueries({ queryKey: ['encounter'] });
    void qc.invalidateQueries({ queryKey: ['prescriptions'] });
    void qc.invalidateQueries({ queryKey: ['dispensary'] });
  };
  return {
    /**
     * Counter eKYC. `appAccount` is required by the API when the record has an
     * app account (else 422 APP_ACCOUNT_CONFIRMATION_REQUIRED); `not_theirs`
     * revokes the app link and removes what the app entered (RL-1118).
     */
    verifyEkyc: useMutation({
      mutationFn: async (v: { id: string; appAccount?: AppAccountAnswer }) =>
        (
          await api.post<PatientDetail>(`/patients/${v.id}/ekyc`, {
            steps: ['chip_read', 'face_match', 'details_confirmed'],
            ...(v.appAccount ? { appAccount: v.appAccount } : {}),
          })
        ).data,
      onSuccess: refresh,
    }),
    /**
     * RL-1185: staff unlink the app account from the record, verified or not
     * (API RL-1184; same effects as eKYC "not theirs"). 201 answers the
     * record (appAccount null, version + 1). `version` is the record read:
     * 409 VERSION_CONFLICT when stale; a second unlink is 404
     * APP_ACCOUNT_NOT_LINKED.
     */
    unlinkAppAccount: useMutation({
      mutationKey: ['patient-app-account-unlink'],
      mutationFn: async (v: { id: string; reason: string; version: number }) =>
        (
          await api.post<PatientDetail>(
            `/patients/${v.id}/app-account/unlink`,
            { reason: v.reason, version: v.version },
          )
        ).data,
      onSuccess: (p) => {
        // The full record page shows the patient from its own query.
        qc.setQueryData<PatientRecord>(['patient-record', p.id], (rec) =>
          rec ? { ...rec, patient: p } : rec,
        );
        refresh(p);
      },
    }),
    addCoverage: useMutation({
      mutationFn: async (v: {
        id: string;
        payerId: string;
        memberNo: string;
        yearlyLimitSen?: number;
        usedSen?: number;
        coPayment?: string;
        notCovered?: string;
        validUntil?: string;
      }) => {
        const { id, ...body } = v;
        return (
          await api.post<PatientDetail>(`/patients/${id}/coverages`, body)
        ).data;
      },
      onSuccess: refresh,
    }),
    endCoverage: useMutation({
      mutationFn: async (v: { id: string; coverageId: string }) =>
        (
          await api.post<PatientDetail>(
            `/patients/${v.id}/coverages/${v.coverageId}/end`,
          )
        ).data,
      onSuccess: refresh,
    }),
    /**
     * RL-1119: confirm a membership the patient entered in the app (card seen),
     * optionally correcting the member number. Only live, unchecked app entries;
     * anything else is 404 COVERAGE_NOT_FOUND (so it is not repeatable).
     */
    checkCoverage: useMutation({
      mutationFn: async (v: {
        id: string;
        coverageId: string;
        memberNo?: string;
      }) =>
        (
          await api.post<PatientDetail>(
            `/patients/${v.id}/coverages/${v.coverageId}/check`,
            v.memberNo ? { memberNo: v.memberNo } : {},
          )
        ).data,
      onSuccess: refresh,
    }),
    /**
     * RL-951: confirm (send the reported value) or correct the blood type.
     * Saved as staff_confirmed; needs the record's current `version` (409
     * VERSION_CONFLICT otherwise) and patients:update.
     */
    setBloodType: useMutation({
      mutationKey: ['patient-blood-type'],
      mutationFn: async (v: {
        id: string;
        version: number;
        bloodType: BloodTypeValue;
      }) =>
        (
          await api.patch<PatientDetail>(`/patients/${v.id}`, {
            version: v.version,
            bloodType: v.bloodType,
          })
        ).data,
      onSuccess: refresh,
    }),
    disclose: useMutation({
      mutationFn: async (v: {
        id: string;
        requester: DisclosureRequester;
        topic: DisclosureTopic;
        identityCheck: DisclosureCheck;
        requesterName?: string;
      }) => {
        const { id, ...body } = v;
        return (
          await api.post<DisclosureResult>(`/patients/${id}/disclosures`, body)
        ).data;
      },
      onSuccess: () =>
        void qc.invalidateQueries({ queryKey: ['patient-disclosures'] }),
    }),
    dismissDuplicate: useMutation({
      mutationFn: async (v: { patientAId: string; patientBId: string }) =>
        (await api.post('/patients/duplicates/dismiss', v)).data,
      onSuccess: () =>
        void qc.invalidateQueries({ queryKey: ['patient-duplicate-pairs'] }),
    }),
    merge: useMutation({
      mutationFn: async (v: {
        keepId: string;
        mergeId: string;
        reason: string;
      }) => (await api.post<PatientDetail>('/patients/merge', v)).data,
      onSuccess: (p) => {
        refresh(p);
        void qc.invalidateQueries({ queryKey: ['patient-duplicate-pairs'] });
      },
    }),
    addAllergy: useMutation({
      mutationFn: async (v: {
        id: string;
        substance: string;
        allergyGroup?: string;
        reaction?: string;
      }) => {
        const { id, ...body } = v;
        return (
          await api.post<PatientDetail>(`/patients/${id}/allergies`, body)
        ).data;
      },
      onSuccess: (p) => {
        refresh(p);
        void qc.invalidateQueries({ queryKey: ['encounter'] });
        void qc.invalidateQueries({ queryKey: ['prescriptions'] });
      },
    }),
    /**
     * RL-951: confirm an allergy the patient reported in the app, optionally
     * setting the prescribing group and severity (omitted = left as is). Only
     * `status: reported` ones (else 409 ALLERGY_NOT_REPORTED); works on any
     * allergy the record lists, merged-record ones too (RL-1171).
     */
    confirmAllergy: useMutation({
      mutationFn: async (v: {
        id: string;
        allergyId: string;
        allergyGroup?: AllergyGroup;
        severity?: AllergySeverity;
      }) => {
        const { id, allergyId, ...body } = v;
        return (
          await api.post<PatientDetail>(
            `/patients/${id}/allergies/${allergyId}/confirm`,
            body,
          )
        ).data;
      },
      onSuccess: allergiesChanged,
    }),
    /** RL-951: reject a patient-reported allergy; the reason is shown to the patient. */
    rejectAllergy: useMutation({
      mutationFn: async (v: {
        id: string;
        allergyId: string;
        reason: string;
      }) =>
        (
          await api.post<PatientDetail>(
            `/patients/${v.id}/allergies/${v.allergyId}/reject`,
            { reason: v.reason },
          )
        ).data,
      onSuccess: allergiesChanged,
    }),
    removeAllergy: useMutation({
      mutationFn: async (v: { id: string; allergyId: string }) =>
        (
          await api.post<PatientDetail>(
            `/patients/${v.id}/allergies/${v.allergyId}/remove`,
          )
        ).data,
      onSuccess: (p) => {
        refresh(p);
        void qc.invalidateQueries({ queryKey: ['encounter'] });
        void qc.invalidateQueries({ queryKey: ['prescriptions'] });
      },
    }),
  };
}
